import React, { useState, useContext, useMemo, useCallback, memo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SupportedLanguage, getFieldTranslations } from "../../types/translations.types";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { RichTextEditor } from "../ui/RichTextEditor";
import { ModalLanguageContext } from "../../context/ModalLanguageContext";

interface TranslatableFieldProps {
 label: string;
 fieldName: string;
 entity: any;
 value: string;
 onChange: (language: SupportedLanguage, value: string) => void;
 type?: "text" | "textarea" | "html" | "rich";
 required?: boolean;
 placeholder?: string;
 className?: string;
 rows?: number;
 height?: string;
}

const TranslatableFieldComponent: React.FC<TranslatableFieldProps> = ({
 label,
 fieldName,
 entity,
 value,
 onChange,
 type = "text",
 required = false,
 placeholder,
 className = "",
 rows = 4,
 height = "300px",
}) => {
 const { t } = useTranslation();
 // Try to use global modal language context, fallback to local state
 const modalLanguageContext = useContext(ModalLanguageContext);
 const [localLanguage, setLocalLanguage] = useState<SupportedLanguage>("en");
 
 // Use global context if available, otherwise use local state
 const currentLanguage: SupportedLanguage = modalLanguageContext?.currentLanguage ?? localLanguage;
 const setCurrentLanguage = modalLanguageContext?.setCurrentLanguage ?? setLocalLanguage;

 // Ref to track input element for focus detection
 const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
 
 // Ref to track previous entity value to detect changes
 const prevEntityValueRef = useRef<string>("");
 
 // Ref to track previous language to detect language changes
 // Initialize to null to detect initial mount
 const prevLanguageRef = useRef<SupportedLanguage | null>(null);
 
 // Ref to track if we're currently updating from user input (to prevent sync overwrite)
 const isUserUpdatingRef = useRef<boolean>(false);
 
 // Ref to track if we're currently syncing due to language change (to prevent ReactQuill onChange from firing)
 const isLanguageSyncingRef = useRef<boolean>(false);

 // Local state to track current value for non-English languages to prevent focus loss
 // This ensures the input value updates immediately even when entity reference changes
 const [localValue, setLocalValue] = useState<string>("");
 
 // Only calculate translations when not in English mode
 // For English, we use the value prop directly, so we don't need translations
 const translations = useMemo(() => {
  if (currentLanguage === "en") {
   // Return a minimal object for English - we won't use it anyway
   return { en: value } as Record<SupportedLanguage, string>;
  }
  // Only calculate translations when needed for non-English languages
  return getFieldTranslations(entity, fieldName as any);
 }, [currentLanguage, entity?.translations?.[fieldName], value, fieldName]);

 const handleLanguageChange = (language: SupportedLanguage) => {
  // Set flag immediately to prevent ReactQuill onChange from firing during language transition
  // This must happen BEFORE setCurrentLanguage because ReactQuill fires onChange synchronously
  if (language !== currentLanguage) {
   isLanguageSyncingRef.current = true;
   // Clear the flag after language sync completes (handled in useEffect)
  }
  setCurrentLanguage(language);
 };
 
 // Don't show language switcher if using global context (it's shown in modal header)
 const showLanguageSwitcher = !modalLanguageContext;

 const handleValueChange = useCallback((newValue: string) => {
  // Ignore onChange events during language sync to prevent ReactQuill from overwriting values
  if (isLanguageSyncingRef.current) {
   return;
  }
  // Update local value immediately for non-English to prevent focus loss
  if (currentLanguage !== "en") {
   // Mark that we're updating from user input to prevent sync from overwriting
   isUserUpdatingRef.current = true;
   setLocalValue(newValue);
   // Reset the flag after a short delay to allow parent update to propagate
   setTimeout(() => {
    isUserUpdatingRef.current = false;
   }, 100);
  }
  onChange(currentLanguage, newValue);
 }, [onChange, currentLanguage, type]);

 // Get current value - use value prop for English, translations for other languages
 // Use value prop directly for English to avoid entity dependency
 // For non-English, use local state to track value and prevent focus loss when entity changes
 const entityValue = currentLanguage === "en" 
  ? value
  : (translations[currentLanguage as keyof typeof translations] || "");

 // Sync local value with entity value when language changes or entity updates
 // But only update if the entity value actually changed (not from our own typing)
 useEffect(() => {
  const languageChanged = prevLanguageRef.current !== null && prevLanguageRef.current !== currentLanguage;
  const isInitialMount = prevLanguageRef.current === null;
  
  // If language changed and flag wasn't set (e.g., language changed through global context),
  // set it now to prevent any pending onChange events
  if (languageChanged && !isLanguageSyncingRef.current) {
   isLanguageSyncingRef.current = true;
  }
  
  if (currentLanguage !== "en") {
   // When language changes, immediately sync with the new language's translation
   if (languageChanged || (isInitialMount && entityValue)) {
    // Language changed or initial mount with non-English - immediately sync with the new language's value
    // Flag is already set in handleLanguageChange, just sync the value
    setLocalValue(entityValue);
    prevEntityValueRef.current = entityValue;
    prevLanguageRef.current = currentLanguage;
    isUserUpdatingRef.current = false;
    // Clear the flag after a short delay to allow ReactQuill to update without triggering onChange
    setTimeout(() => {
     isLanguageSyncingRef.current = false;
    }, 200);
    return;
   }
   
   // Don't sync if we're currently updating from user input
   if (isUserUpdatingRef.current) {
    // Still update the ref to track entity value, but don't overwrite local value
    prevEntityValueRef.current = entityValue;
    return;
   }
   
   // Only update local value if entity value changed and we're not currently focused
   // This prevents overwriting user input while typing
   const isFocused = document.activeElement === inputRef.current;
   const entityValueChanged = entityValue !== prevEntityValueRef.current;
   
   // Sync conditions:
   // 1. Initial load: localValue is empty and entityValue has a value
   // 2. External update: entityValue changed from parent AND we're not focused
   //    BUT: Don't sync back if user just cleared the input (localValue is "" and entityValue still has old value)
   const isInitialLoad = !localValue && entityValue;
   const isExternalUpdate = entityValueChanged && !isFocused;
   
   // Only sync if it's an initial load, or if it's an external update that's not trying to restore a cleared value
   if (isInitialLoad || (isExternalUpdate && !(localValue === "" && prevEntityValueRef.current !== ""))) {
    setLocalValue(entityValue);
   }
   
   // Update ref to track entity value changes
   prevEntityValueRef.current = entityValue;
  } else {
   // Reset local value when switching to English
   // Flag is already set in handleLanguageChange if language changed
   setLocalValue("");
   prevEntityValueRef.current = "";
   isUserUpdatingRef.current = false;
   if (prevLanguageRef.current !== currentLanguage && prevLanguageRef.current !== null) {
    // Clear the flag after a short delay
    setTimeout(() => {
     isLanguageSyncingRef.current = false;
    }, 200);
    prevLanguageRef.current = currentLanguage;
   } else if (prevLanguageRef.current !== currentLanguage) {
    prevLanguageRef.current = currentLanguage;
   }
  }
 }, [currentLanguage, entityValue, localValue]); // Include localValue to detect when it's empty

 // Use local value for non-English to prevent focus loss, entity value for English
 const currentValue = currentLanguage === "en" ? value : localValue;

 return (
  <div className={`space-y-2 ${className}`}>
   <div className="flex items-center justify-between">
    <label className="block text-sm font-semibold text-gray-700">
     {label}
     {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {showLanguageSwitcher && (
     <LanguageSwitcher
      currentLanguage={currentLanguage}
      onLanguageChange={handleLanguageChange}
      showLabel={false}
     />
    )}
   </div>

   {type === "rich" ? (
    <RichTextEditor
     key={`${fieldName}-${currentLanguage}`}
     value={currentValue}
     onChange={handleValueChange}
     placeholder={placeholder || t("common.enterInLanguage", { field: label.toLowerCase(), language: currentLanguage.toUpperCase() })}
     height={height}
    />
   ) : type === "textarea" ? (
    <textarea
     ref={(el) => {
      inputRef.current = el;
     }}
     value={currentValue}
     onChange={(e) => handleValueChange(e.target.value)}
     placeholder={placeholder || t("common.enterInLanguage", { field: label.toLowerCase(), language: currentLanguage.toUpperCase() })}
     required={required && currentLanguage === "en"}
     rows={rows}
     className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
     autoComplete="off"
    />
   ) : type === "html" ? (
    <div className="space-y-2">
     <textarea
      value={currentValue}
      onChange={(e) => handleValueChange(e.target.value)}
      placeholder={placeholder || t("common.enterInLanguage", { field: label.toLowerCase(), language: currentLanguage.toUpperCase() })}
      required={required && currentLanguage === "en"}
      rows={rows}
      className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
      autoComplete="off"
     />
     <div className="text-xs text-gray-500">
      {t("common.preview")} ({currentLanguage.toUpperCase()}):
     </div>
     <div
      className="border border-gray-200 p-4 bg-gray-50 min-h-[100px] prose prose-sm max-w-none"
      dangerouslySetInnerHTML={{ __html: currentValue }}
     />
    </div>
   ) : (
    <input
     ref={(el) => {
      inputRef.current = el;
     }}
     type="text"
     value={currentValue}
     onChange={(e) => handleValueChange(e.target.value)}
     placeholder={placeholder || t("common.enterInLanguage", { field: label.toLowerCase(), language: currentLanguage.toUpperCase() })}
     required={required && currentLanguage === "en"}
     className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
     autoComplete="off"
    />
   )}

   {currentLanguage !== "en" && !currentValue && (
    <p className="text-xs text-gray-500 italic">
     {t("common.emptyFallback")}: "{value}"
    </p>
   )}
  </div>
 );
};

// Memoize the component to prevent re-renders when props haven't meaningfully changed
// Returns true if props are equal (skip re-render), false if different (re-render)
export const TranslatableField = memo(TranslatableFieldComponent, (prevProps, nextProps) => {
 // Always re-render if these critical props change
 if (prevProps.value !== nextProps.value) return false; // Value changed, re-render
 if (prevProps.fieldName !== nextProps.fieldName) return false;
 if (prevProps.type !== nextProps.type) return false;
 if (prevProps.onChange !== nextProps.onChange) return false; // Handler changed, re-render
 if (prevProps.label !== nextProps.label) return false;
 if (prevProps.required !== nextProps.required) return false;
 
 // For English mode, we use the value prop, so we don't need to check entity field value
 // Only check translations (for non-English languages)
 const prevTranslations = prevProps.entity?.translations?.[prevProps.fieldName];
 const nextTranslations = nextProps.entity?.translations?.[nextProps.fieldName];
 
 // Deep compare translation objects - only re-render if translations actually changed
 if (prevTranslations !== nextTranslations) {
  if (!prevTranslations || !nextTranslations) return false; // One exists, other doesn't
  const prevKeys = Object.keys(prevTranslations);
  const nextKeys = Object.keys(nextTranslations);
  if (prevKeys.length !== nextKeys.length) return false;
  for (const key of prevKeys) {
   if (prevTranslations[key] !== nextTranslations[key]) return false;
  }
 }
 
 // All meaningful props are the same, skip re-render
 return true;
});

